import { useMemo, useState, type ReactNode } from 'react'
import { useParams } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import {
  AlertCircle,
  BookOpen,
  Briefcase,
  Building2,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  ClipboardCheck,
  type LucideIcon,
  Eye,
  FileText,
  Loader2,
  MessageSquareQuote,
  Minus,
  Plug,
  Plus,
  Server,
  ShieldAlert,
  Tag,
  User,
} from 'lucide-react'
import {
  openMainPanelOverlayAtom,
  type SystemDetailOverlayPayload,
  type ToolDetailOverlayPayload,
} from '@/stores/mainPanelOverlayStore'
import { ScrollArea, ScrollBar } from '@deepguide-ai/ui/scroll-area'
import {
  Marker,
  MarkerContent,
  MarkerIcon,
} from '@/components/ai-elements/marker'
import { useChatStopped } from '@/components/chat/chat-stopped-context'

export interface ToolPartProps {
  part: any
  icon?: ReactNode
  shouldPoll?: boolean
  /** Forwarded from messages.tsx — see ScreenataChatToolMessages.ts. */
  isLastAssistantMessage?: boolean
}

export interface ToolState {
  isComplete: boolean
  isError: boolean
  result: any
  args: any
  rawError: string | null
  /**
   * `needsApproval` tools live in an in-between state while the user
   * decides on the in-chat approval card: input ready, output not yet
   * available. The AI SDK reports this via `part.state === 'approval-requested'`
   * or by attaching a `part.approval` object (the latter sticks around
   * after stream stop). Consumers like ToolStatusLine use this to
   * suppress the misleading "Failed:" label that would otherwise fire
   * when `chatStopped && !isComplete`.
   */
  awaitingApproval: boolean
}

function toErrorString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value
  }
  if (
    value &&
    typeof value === 'object' &&
    'message' in value &&
    typeof value.message === 'string' &&
    value.message.trim().length > 0
  ) {
    return value.message
  }
  return null
}

export function readToolState(part: any): ToolState {
  if (part?.type === 'tool-input-error') {
    return {
      isComplete: true,
      isError: true,
      result: null,
      args: part.input ?? {},
      rawError: toErrorString(part.errorText) ?? toErrorString(part.error),
      awaitingApproval: false,
    }
  }
  if (part?.state) {
    const isComplete =
      part.state === 'output-available' || part.state === 'error'
    const isError = part.state === 'error'
    const rawOutput = isComplete
      ? (part.output ?? part.errorText ?? part.error ?? null)
      : null
    let result: unknown = null
    if (isComplete && rawOutput != null) {
      try {
        result =
          typeof rawOutput === 'string' ? JSON.parse(rawOutput) : rawOutput
      } catch {
        /* ignore */
      }
    }
    const awaitingApproval =
      !isComplete &&
      (part.state === 'approval-requested' || part.approval != null)
    return {
      isComplete,
      isError,
      result,
      args: part.input ?? {},
      rawError: toErrorString(part.errorText) ?? toErrorString(part.error),
      awaitingApproval,
    }
  }
  if (part?.toolInvocation) {
    const ti = part.toolInvocation
    const isComplete = ti.state === 'result' || ti.state === 'error'
    const isError = ti.state === 'error'
    let result: unknown = null
    if (isComplete && ti.result) {
      try {
        result =
          typeof ti.result === 'string' ? JSON.parse(ti.result) : ti.result
      } catch {
        /* ignore */
      }
    }
    return {
      isComplete,
      isError,
      result,
      args: ti.args ?? {},
      rawError: toErrorString(ti.error) ?? toErrorString(ti.result),
      awaitingApproval: false,
    }
  }
  return {
    isComplete: false,
    isError: false,
    result: null,
    args: {},
    rawError: null,
    awaitingApproval: false,
  }
}

export function isToolError(state: ToolState): boolean {
  if (!state.isComplete || !state.result) return false
  return (
    state.result.ok === false ||
    state.result.success === false ||
    !!state.result.error
  )
}

export function plural(
  n: number,
  singular: string,
  pluralForm?: string
): string {
  return `${n} ${n === 1 ? singular : (pluralForm ?? `${singular}s`)}`
}

export function pickString(...vals: unknown[]): string | null {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim().length > 0) return v
  }
  return null
}

function getToolErrorMessage(state: ToolState): string | null {
  if (!state.isComplete) return null
  return pickString(
    state.result?.error,
    state.result?.message,
    state.result?.errorCode,
    state.rawError
  )
}

const ICON_CLS = 'h-3.5 w-3.5 shrink-0 text-text-placeholder'

export interface ToolListPreviewItem {
  id?: string | null
  title: string
  subtitle?: string | null
  meta?: Array<string | null | undefined>
  href?: string | null
  detailOverlay?: {
    id: string
    payload: ToolDetailOverlayPayload
  } | null
  systemDetailOverlay?: {
    id: string
    payload: SystemDetailOverlayPayload
  } | null
  tone?: 'default' | 'critical' | 'high' | 'medium' | 'low' | 'success'
}

function toneClassName(tone: ToolListPreviewItem['tone']): string {
  switch (tone) {
    case 'critical':
      return 'text-status-red'
    case 'high':
      return 'text-orange-600'
    case 'medium':
      return 'text-status-orange'
    case 'low':
      return 'text-emerald-600'
    case 'success':
      return 'text-emerald-600'
    default:
      return 'text-muted-foreground'
  }
}

function compactMeta(meta: ToolListPreviewItem['meta']): string[] {
  return (meta ?? []).filter(
    (item): item is string => typeof item === 'string' && item.trim().length > 0
  )
}

function titleFromEmptyLabel(emptyLabel: string): string {
  const cleaned = emptyLabel
    .replace(/^No\s+/i, '')
    .replace(/\s+(found|matched|returned)$/i, '')
    .trim()

  if (!cleaned) return 'Results'
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

export function normalizeTone(value: unknown): ToolListPreviewItem['tone'] {
  if (typeof value !== 'string') return 'default'
  switch (value.toLowerCase()) {
    case 'critical':
      return 'critical'
    case 'high':
      return 'high'
    case 'medium':
    case 'moderate':
      return 'medium'
    case 'low':
      return 'low'
    case 'approved':
    case 'closed':
    case 'complete':
    case 'completed':
    case 'mitigated':
      return 'success'
    default:
      return 'default'
  }
}

export function getToolCallId(part: any, fallback: string): string {
  return (
    pickString(
      part?.toolCallId,
      part?.id,
      part?.toolInvocation?.toolCallId,
      part?.toolInvocation?.id
    ) ?? fallback
  )
}

export function formatToolValue(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value : null
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return null
}

const ENTITY_ICON: Record<ToolDetailOverlayPayload['entityType'], LucideIcon> = {
  risk: ShieldAlert,
  asset: Server,
  vendor: Building2,
  finding: AlertCircle,
  ticket: Tag,
  policy: FileText,
  document: FileText,
  claim: MessageSquareQuote,
  test: CheckSquare,
  evidence: ClipboardCheck,
  identity: User,
  integration: Plug,
  program: Briefcase,
  runbook: BookOpen,
}

const ENTITY_LABEL: Record<ToolDetailOverlayPayload['entityType'], string> = {
  risk: 'Risk',
  asset: 'Asset',
  vendor: 'Vendor',
  finding: 'Finding',
  ticket: 'Ticket',
  policy: 'Policy',
  document: 'Document',
  claim: 'Claim',
  test: 'Test',
  evidence: 'Evidence',
  identity: 'Identity',
  integration: 'Integration',
  program: 'Program',
  runbook: 'Procedure',
}

export function ToolDetailOverlayLink({
  overlayId,
  payload,
  systemDetail,
  label = 'View',
}: {
  overlayId: string
  payload: ToolDetailOverlayPayload
  systemDetail?: SystemDetailOverlayPayload | null
  label?: string
}) {
  const openOverlay = useSetAtom(openMainPanelOverlayAtom)
  const routeParams = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const resolvedSystemDetail = systemDetail
    ? {
        ...systemDetail,
        workspaceId: systemDetail.workspaceId || routeParams.workspaceId || '',
      }
    : null

  const Icon = ENTITY_ICON[payload.entityType] ?? FileText
  const kindLabel = ENTITY_LABEL[payload.entityType] ?? 'Detail'
  const subtitleParts = [kindLabel, payload.subtitle?.trim() || null].filter(
    (s): s is string => !!s
  )

  return (
    <div
      className='mt-1 flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-border-divider hover:bg-surface-subtle'
      
    >
      <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background text-text-subtle'>
        <Icon className='h-4 w-4' />
      </span>
      <span className='flex min-w-0 flex-1 flex-col'>
        <span className='truncate text-[12px] font-medium text-text-secondary'>
          {payload.title?.trim() || 'Untitled'}
        </span>
        <span className='truncate text-[11px] text-text-subtle'>
          {subtitleParts.join(' · ')}
        </span>
      </span>
      <button className='inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-text-body transition-colors hover:bg-muted hover:text-text-secondary'
      onClick={() =>
        openOverlay(
          resolvedSystemDetail
            ? {
                kind: 'system-detail',
                id: overlayId,
                source: 'chat',
                payload: resolvedSystemDetail,
              }
            : {
                kind: 'tool-detail',
                id: overlayId,
                source: 'chat',
                payload,
              }
        )
      }
       >
        <Eye className='h-3.5 w-3.5' />
        {label}
      </button>
    </div>
  )
}

export function ToolListPreview({
  items,
  maxItems = 5,
  emptyLabel,
  moreLabel,
  title,
  columns,
  variant = 'table',
}: {
  items: ToolListPreviewItem[]
  maxItems?: number
  emptyLabel: string
  moreLabel?: string | null
  title?: string
  columns?: string[]
  variant?: 'table' | 'compact'
}) {
  const openOverlay = useSetAtom(openMainPanelOverlayAtom)
  const resolvedTitle = title?.trim() || titleFromEmptyLabel(emptyLabel)
  const visibleItems = items.slice(0, maxItems)
  const hiddenCount = Math.max(0, items.length - visibleItems.length)
  const computedMoreLabel =
    moreLabel ?? (hiddenCount > 0 ? `${hiddenCount} more` : null)
  const rows = visibleItems.map((item) => ({
    item,
    meta: compactMeta(item.meta),
  }))
  const showSubtitle = rows.some(({ item }) => !!item.subtitle)
  const showPrimaryMeta = rows.some(({ meta }) => !!meta[0])
  const showSecondaryMeta = rows.some(({ meta }) => !!meta[1])
  const showTertiaryMeta = rows.some(({ meta }) => !!meta[2])
  const columnLabels = [
    columns?.[0] ?? 'Name',
    showSubtitle ? (columns?.[1] ?? 'Type') : null,
    showPrimaryMeta ? (columns?.[showSubtitle ? 2 : 1] ?? 'Status') : null,
    showSecondaryMeta
      ? (columns?.[showSubtitle ? 3 : 2] ?? 'Owner')
      : null,
    showTertiaryMeta
      ? (columns?.[showSubtitle ? 4 : 3] ?? 'Reference')
      : null,
  ].filter(Boolean)
  const gridColumns = [
    'minmax(16rem,1fr)',
    showSubtitle ? '8rem' : null,
    showPrimaryMeta ? '5.5rem' : null,
    showSecondaryMeta ? '6rem' : null,
    showTertiaryMeta ? '8rem' : null,
  ]
    .filter(Boolean)
    .join(' ')

  if (variant === 'compact') {
    if (items.length === 0) {
      return (
        <div className='border-border/70 bg-background mt-1 w-full overflow-hidden rounded-md border'>
          <div className='flex items-center justify-between gap-3 border-b border-border/70 px-2.5 py-1.5'>
            <div className='truncate text-[12px] font-semibold text-text-secondary'>
              {resolvedTitle}
            </div>
            <div className='shrink-0 text-[11px] font-medium text-text-subtle'>
              0 items
            </div>
          </div>
          <div className='px-2.5 py-1.5 text-xs text-muted-foreground/80'>
            {emptyLabel}
          </div>
        </div>
      )
    }

    return (
      <div className='border-border/70 bg-background mt-1 w-full overflow-hidden rounded-md border'>
        <ScrollArea className='w-full' type='always' viewClassName='w-full'>
          <div className='divide-border/70 min-w-[42rem] divide-y'>
            <div
              className='grid min-w-0 gap-3 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-text-subtle'
              style={{ gridTemplateColumns: gridColumns }}
            >
              {columnLabels.map((label) => (
                <span className='truncate' key={label}>
                  {label}
                </span>
              ))}
            </div>
            {rows.map(({ item, meta }, index) => {
              const [primaryMeta, secondaryMeta, tertiaryMeta] = meta
              const openDetail = item.systemDetailOverlay
                ? () =>
                    openOverlay({
                      kind: 'system-detail',
                      id: item.systemDetailOverlay!.id,
                      source: 'chat',
                      payload: item.systemDetailOverlay!.payload,
                    })
                : item.detailOverlay
                  ? () =>
                      openOverlay({
                        kind: 'tool-detail',
                        id: item.detailOverlay!.id,
                        source: 'chat',
                        payload: item.detailOverlay!.payload,
                      })
                  : null
              return (
                <div
                  className={`grid min-w-0 gap-3 px-2.5 py-1.5 text-xs ${
                    openDetail
                      ? 'cursor-pointer transition-colors hover:bg-background'
                      : ''
                  }`}
                  key={`${item.id ?? item.title}-${index}`}
                  onClick={openDetail ?? undefined}
                  onKeyDown={(event) => {
                    if (!openDetail) return
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    openDetail()
                  }}
                  role={openDetail ? 'button' : undefined}
                  style={{ gridTemplateColumns: gridColumns }}
                  tabIndex={openDetail ? 0 : undefined}
                >
                  <div className='min-w-0'>
                    {item.href ? (
                      <a
                        className='block min-w-0 hover:underline'
                        href={item.href}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                        rel='noreferrer'
                        target='_blank'
                      >
                        <span className='block truncate text-[12px] font-medium text-text-secondary'>
                          {item.title}
                        </span>
                      </a>
                    ) : (
                      <div
                        className={`flex min-w-0 items-center gap-1.5 truncate text-[12px] font-medium ${
                          openDetail ? 'text-cobalt-strong' : 'text-text-secondary'
                        }`}
                      >
                        {openDetail ? (
                          <Eye className='h-3.5 w-3.5 shrink-0' />
                        ) : null}
                        <span className='truncate'>{item.title}</span>
                      </div>
                    )}
                  </div>
                  {showSubtitle ? (
                    <span className='text-muted-foreground truncate text-[11px]'>
                      {item.subtitle ?? ''}
                    </span>
                  ) : null}
                  {showPrimaryMeta ? (
                    <span
                      className={`truncate text-[11px] font-medium ${toneClassName(item.tone)}`}
                    >
                      {primaryMeta ?? ''}
                    </span>
                  ) : null}
                  {showSecondaryMeta ? (
                    <span className='text-muted-foreground truncate text-[11px]'>
                      {secondaryMeta ?? ''}
                    </span>
                  ) : null}
                  {showTertiaryMeta ? (
                    <span className='text-muted-foreground truncate text-[11px]'>
                      {tertiaryMeta ?? ''}
                    </span>
                  ) : null}
                </div>
              )
            })}
          </div>
          <ScrollBar orientation='horizontal' />
        </ScrollArea>
        {computedMoreLabel ? (
          <div className='text-muted-foreground border-border/70 border-t px-2.5 py-1 text-xs'>
            + {computedMoreLabel}
          </div>
        ) : null}
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className='mt-1 text-xs text-muted-foreground/80'>
        {emptyLabel}
      </div>
    )
  }

  return (
    <div className='mt-1 w-full overflow-hidden rounded-md border border-border bg-card'>
      <ScrollArea className='w-full' type='always' viewClassName='w-full'>
        <div className='divide-y divide-border min-w-[42rem]'>
          <div
            className='grid min-w-0 gap-3 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-text-subtle'
            style={{ gridTemplateColumns: gridColumns }}
          >
            {columnLabels.map((label) => (
              <span className='truncate' key={label}>
                {label}
              </span>
            ))}
          </div>
          {rows.map(({ item, meta }, index) => {
            const [primaryMeta, secondaryMeta, tertiaryMeta] = meta
            const openDetail = item.systemDetailOverlay
              ? () =>
                  openOverlay({
                    kind: 'system-detail',
                    id: item.systemDetailOverlay!.id,
                    source: 'chat',
                    payload: item.systemDetailOverlay!.payload,
                  })
              : item.detailOverlay
                ? () =>
                    openOverlay({
                      kind: 'tool-detail',
                      id: item.detailOverlay!.id,
                      source: 'chat',
                      payload: item.detailOverlay!.payload,
                    })
                : null
            return (
              <div
                className={`grid min-w-0 gap-3 px-3 py-2 text-xs ${
                  openDetail
                    ? 'cursor-pointer transition-colors hover:bg-surface-subtle'
                    : ''
                }`}
                key={`${item.id ?? item.title}-${index}`}
                onClick={openDetail ?? undefined}
                onKeyDown={(event) => {
                  if (!openDetail) return
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  openDetail()
                }}
                role={openDetail ? 'button' : undefined}
                style={{ gridTemplateColumns: gridColumns }}
                tabIndex={openDetail ? 0 : undefined}
              >
                <div className='min-w-0'>
                  {item.href ? (
                    <a
                      className='block min-w-0 rounded-md text-text-secondary transition-colors hover:text-text-secondary hover:underline'
                      href={item.href}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                      rel='noreferrer'
                      target='_blank'
                    >
                      <span className='block truncate text-[12px] font-medium text-text-secondary'>
                        {item.title}
                      </span>
                    </a>
                  ) : (
                    <div className='flex min-w-0 items-center gap-1.5 truncate text-[12px] font-medium text-text-secondary'>
                      {openDetail ? (
                        <Eye className='h-3.5 w-3.5 shrink-0' />
                      ) : null}
                      <span className='truncate'>{item.title}</span>
                    </div>
                  )}
                </div>
                {showSubtitle ? (
                  <span className='text-muted-foreground truncate text-[11px]'>
                    {item.subtitle ?? ''}
                  </span>
                ) : null}
                {showPrimaryMeta ? (
                  <span
                    className={`truncate text-[11px] font-medium ${toneClassName(item.tone)}`}
                  >
                    {primaryMeta ?? ''}
                  </span>
                ) : null}
                {showSecondaryMeta ? (
                  <span className='text-muted-foreground truncate text-[11px]'>
                    {secondaryMeta ?? ''}
                  </span>
                ) : null}
                {showTertiaryMeta ? (
                  <span className='text-muted-foreground truncate text-[11px]'>
                    {tertiaryMeta ?? ''}
                  </span>
                ) : null}
              </div>
            )
          })}
        </div>
        <ScrollBar orientation='horizontal' />
      </ScrollArea>
      {computedMoreLabel ? (
        <div className='border-t border-border px-2.5 py-1 text-xs text-muted-foreground'>
          + {computedMoreLabel}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Shared single-line status renderer. Each per-action ToolMessage component
 * decides its own labels and detail, then delegates rendering through here.
 */
export function ToolStatusLine({
  state,
  loadingLabel,
  completedLabel,
  loadingDetail,
  completedDetail,
  completedIcon,
}: {
  state: ToolState
  loadingLabel: string
  completedLabel: string
  loadingDetail?: string | null
  completedDetail?: string | null
  /** Icon shown when the tool completes successfully. Defaults to a checkmark. */
  completedIcon?: ReactNode
}) {
  const chatStopped = useChatStopped()
  const [showDetails, setShowDetails] = useState(false)
  const [hovered, setHovered] = useState(false)
  const errored = state.isError || isToolError(state)
  // `chatStopped && !isComplete` looks like a stall, but for tools paused
  // on the in-chat approval card it's the *normal* terminal state until
  // the user clicks Submit / Cancel. Don't paint those "Failed:" — the
  // ApprovalRequestCard is the real UI and already explains what's
  // happening. The per-tool message row collapses (returns null) so we
  // don't double-stack a misleading status line above the card.
  if (state.awaitingApproval && !errored) {
    return null
  }
  const stalled = chatStopped && !state.isComplete
  const failed = errored || stalled
  const detail = state.isComplete ? completedDetail : loadingDetail
  const errorMessage = failed ? getToolErrorMessage(state) : null

  let iconNode: ReactNode
  if (failed) {
    iconNode = <CircleAlert className={`${ICON_CLS} text-status-orange`} />
  } else if (state.isComplete) {
    iconNode = completedIcon ?? (
      <CheckCircle2 className={ICON_CLS} />
    )
  } else {
    iconNode = <Loader2 className={`${ICON_CLS} animate-spin`} />
  }

  // Failed state: show only the concise label. The verbose error reason (often
  // a multi-sentence agent instruction) collapses behind an inline chevron on
  // the label line (no "Details" text); the chevron flips direction on toggle.
  // Hovering swaps the leading alert icon for a +/- expand/collapse affordance.
  if (failed) {
    const failedLabel = `Failed: ${loadingLabel.replace(/\.+$/, '')}`
    if (!errorMessage) {
      return (
        <Marker className='text-foreground items-start py-0 text-sm'>
          <MarkerIcon className='mt-0.5'>{iconNode}</MarkerIcon>
          <MarkerContent className='flex-1 break-words whitespace-normal'>
            {failedLabel}
          </MarkerContent>
        </Marker>
      )
    }
    // Default icon is the alert; hovering the label swaps it for a +/- expand
    // affordance. JS hover state (not CSS group-hover) keeps the default
    // deterministic and scopes the hit area to the label, not the whole row.
    const leadingIcon = hovered ? (
      showDetails ? (
        <Minus className='h-3.5 w-3.5 text-status-orange' />
      ) : (
        <Plus className='h-3.5 w-3.5 text-status-orange' />
      )
    ) : (
      iconNode
    )
    return (
      <div className='text-foreground py-0 text-sm'>
        <button
          type='button'
          onClick={() => setShowDetails((open) => !open)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className='inline-flex max-w-full text-left'
        >
          <Marker className='text-foreground items-start py-0'>
            <MarkerIcon className='mt-0.5'>{leadingIcon}</MarkerIcon>
            <MarkerContent className='flex items-center gap-1 break-words whitespace-normal'>
              {failedLabel}
            </MarkerContent>
            {showDetails ? (
              <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ) : (
              <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            )}
          </Marker>
        </button>
        {showDetails ? (
          <p className='mt-1 ml-[22px] break-words whitespace-pre-wrap text-[12px] text-text-subtle'>
            {detail ? `${detail}. ` : ''}
            {errorMessage}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <Marker className='text-foreground py-0 text-sm'>
      <MarkerIcon>{iconNode}</MarkerIcon>
      <MarkerContent>
        {state.isComplete ? completedLabel : loadingLabel}
        {detail ? ` · ${detail}` : ''}
      </MarkerContent>
    </Marker>
  )
}

/**
 * Bag delegate: clones a part with `args.action` injected, then forwards to
 * an existing bag component. Used for actions whose UI lives in a richer
 * bag renderer (cards, expandable lists, embedded panels).
 */
export function BagDelegate({
  Component,
  action,
  part,
  icon,
  shouldPoll,
  isLastAssistantMessage,
}: {
  Component: React.ComponentType<ToolPartProps>
  action: string
  part: any
  icon?: ReactNode
  shouldPoll?: boolean
  isLastAssistantMessage?: boolean
}) {
  const wrapped = useMemo(() => {
    if (!part || typeof part !== 'object') return part
    if (part.toolInvocation) {
      return {
        ...part,
        toolInvocation: {
          ...part.toolInvocation,
          args: { action, ...(part.toolInvocation.args ?? {}) },
        },
      }
    }
    return {
      ...part,
      input: { action, ...(part.input ?? {}) },
    }
  }, [part, action])
  return (
    <Component
      part={wrapped}
      icon={icon}
      shouldPoll={shouldPoll}
      isLastAssistantMessage={isLastAssistantMessage}
    />
  )
}
